import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Brain, Printer, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const WHATSAPP_NUMBER = '919911922763'; // MIND FRAME WhatsApp

const initialState = {
  client_name: '',
  client_email: '',
  client_phone: '',
  client_age: '',
  // Medical
  current_medications: '',
  past_medications: '',
  medical_conditions: '',
  allergies: '',
  // Mental Health
  previous_therapy: '',
  previous_diagnosis: '',
  hospitalization_history: '',
  family_mental_health: '',
  // Current
  current_symptoms: '',
  symptom_duration: '',
  triggers: '',
  coping_mechanisms: '',
  // Goals
  therapy_goals: '',
  expectations: '',
  // Lifestyle
  sleep_pattern: '',
  exercise_routine: '',
  substance_use: '',
  support_system: '',
  // Safety
  suicidal_thoughts: '',
  self_harm: '',
  emergency_plan: ''
};

const PublicIntakeForm = () => {
  const [formData, setFormData] = useState(initialState);

  useEffect(() => {
    document.title = 'Client Intake Form - MIND FRAME';
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSaveAsPDF = () => {
    if (!formData.client_name.trim()) {
      toast.error('Please enter your name first');
      return;
    }
    if (!formData.current_symptoms.trim() || !formData.therapy_goals.trim()) {
      toast.error('Please fill the required (*) fields before saving');
      return;
    }
    toast.success('Choose "Save as PDF" in the print dialog');
    setTimeout(() => window.print(), 400);
  };

  const handleSendWhatsApp = () => {
    if (!formData.client_name.trim() || !formData.current_symptoms.trim() || !formData.therapy_goals.trim()) {
      toast.error('Please fill at least Name, Current Symptoms, and Therapy Goals');
      return;
    }
    const message = `*MIND FRAME — Client Intake Form*

*Personal Details*
Name: ${formData.client_name}
Email: ${formData.client_email || '—'}
Phone: ${formData.client_phone || '—'}
Age: ${formData.client_age || '—'}

*Medical History*
Current Medications: ${formData.current_medications || '—'}
Past Medications: ${formData.past_medications || '—'}
Medical Conditions: ${formData.medical_conditions || '—'}
Allergies: ${formData.allergies || '—'}

*Mental Health History*
Previous Therapy: ${formData.previous_therapy || '—'}
Previous Diagnoses: ${formData.previous_diagnosis || '—'}
Psychiatric Hospitalization: ${formData.hospitalization_history || '—'}
Family Mental Health History: ${formData.family_mental_health || '—'}

*Current Situation*
Symptoms / Concerns: ${formData.current_symptoms}
Duration: ${formData.symptom_duration || '—'}
Triggers: ${formData.triggers || '—'}
Coping Strategies: ${formData.coping_mechanisms || '—'}

*Goals*
Therapy Goals: ${formData.therapy_goals}
Expectations: ${formData.expectations || '—'}

*Lifestyle*
Sleep: ${formData.sleep_pattern || '—'}
Exercise: ${formData.exercise_routine || '—'}
Substance Use: ${formData.substance_use || '—'}
Support System: ${formData.support_system || '—'}

*Safety Assessment*
Thoughts of self-harm: ${formData.suicidal_thoughts || '—'}
History of self-harm: ${formData.self_harm || '—'}
Emergency plan: ${formData.emergency_plan || '—'}`;

    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 py-12 px-6 print:bg-white print:py-4">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          textarea, input { border: 1px solid #cbd5e1 !important; background: white !important; }
          .print-keep-color { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center print-keep-color">
              <Brain className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800">MIND FRAME</h1>
          </div>
          <h2 className="text-2xl font-semibold text-slate-700 mb-2">Client Intake Form</h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Please complete this form before your first session. You can fill it on this page and then
            either <strong>Save as PDF</strong> to email it, or <strong>Send via WhatsApp</strong> directly.
          </p>
        </div>

        <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
          {/* Personal Details */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Details</CardTitle>
              <CardDescription>Basic information about you</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client_name">Full Name *</Label>
                <Input id="client_name" name="client_name" data-testid="public-intake-name" value={formData.client_name} onChange={handleChange} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_age">Age</Label>
                <Input id="client_age" name="client_age" type="number" value={formData.client_age} onChange={handleChange} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_email">Email</Label>
                <Input id="client_email" name="client_email" type="email" value={formData.client_email} onChange={handleChange} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_phone">Phone</Label>
                <Input id="client_phone" name="client_phone" value={formData.client_phone} onChange={handleChange} />
              </div>
            </CardContent>
          </Card>

          {/* Medical History */}
          <Card>
            <CardHeader>
              <CardTitle>Medical History</CardTitle>
              <CardDescription>Information about your physical health</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_medications">Current Medications (if any)</Label>
                <Textarea id="current_medications" name="current_medications" value={formData.current_medications} onChange={handleChange} placeholder="List any medications you're currently taking" rows={3} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="past_medications">Past Medications</Label>
                <Textarea id="past_medications" name="past_medications" value={formData.past_medications} onChange={handleChange} rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="medical_conditions">Medical Conditions</Label>
                <Textarea id="medical_conditions" name="medical_conditions" value={formData.medical_conditions} onChange={handleChange} rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="allergies">Allergies</Label>
                <Input id="allergies" name="allergies" value={formData.allergies} onChange={handleChange} />
              </div>
            </CardContent>
          </Card>

          {/* Mental Health History */}
          <Card>
            <CardHeader>
              <CardTitle>Mental Health History</CardTitle>
              <CardDescription>Your previous mental health experiences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="previous_therapy">Previous Therapy / Counselling Experience</Label>
                <Textarea id="previous_therapy" name="previous_therapy" value={formData.previous_therapy} onChange={handleChange} rows={3} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="previous_diagnosis">Previous Mental Health Diagnoses</Label>
                <Textarea id="previous_diagnosis" name="previous_diagnosis" value={formData.previous_diagnosis} onChange={handleChange} rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hospitalization_history">Psychiatric Hospitalization History</Label>
                <Textarea id="hospitalization_history" name="hospitalization_history" value={formData.hospitalization_history} onChange={handleChange} rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="family_mental_health">Family Mental Health History</Label>
                <Textarea id="family_mental_health" name="family_mental_health" value={formData.family_mental_health} onChange={handleChange} rows={2} />
              </div>
            </CardContent>
          </Card>

          {/* Current Situation */}
          <Card>
            <CardHeader>
              <CardTitle>Current Situation</CardTitle>
              <CardDescription>Tell us about what brings you here today</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_symptoms">Current Symptoms / Concerns *</Label>
                <Textarea id="current_symptoms" name="current_symptoms" data-testid="public-intake-symptoms" value={formData.current_symptoms} onChange={handleChange} required rows={4} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="symptom_duration">How long have you experienced these symptoms?</Label>
                <Input id="symptom_duration" name="symptom_duration" value={formData.symptom_duration} onChange={handleChange} placeholder="e.g., 6 months, 2 years" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="triggers">Triggers or Patterns</Label>
                <Textarea id="triggers" name="triggers" value={formData.triggers} onChange={handleChange} rows={3} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="coping_mechanisms">Current Coping Strategies</Label>
                <Textarea id="coping_mechanisms" name="coping_mechanisms" value={formData.coping_mechanisms} onChange={handleChange} rows={3} />
              </div>
            </CardContent>
          </Card>

          {/* Goals */}
          <Card>
            <CardHeader>
              <CardTitle>Goals and Expectations</CardTitle>
              <CardDescription>What you hope to achieve through counselling</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="therapy_goals">What are your goals for therapy? *</Label>
                <Textarea id="therapy_goals" name="therapy_goals" data-testid="public-intake-goals" value={formData.therapy_goals} onChange={handleChange} required rows={4} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expectations">Expectations from counselling</Label>
                <Textarea id="expectations" name="expectations" value={formData.expectations} onChange={handleChange} rows={3} />
              </div>
            </CardContent>
          </Card>

          {/* Lifestyle */}
          <Card>
            <CardHeader>
              <CardTitle>Lifestyle Information</CardTitle>
              <CardDescription>Your daily habits and routines</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sleep_pattern">Sleep Pattern</Label>
                <Textarea id="sleep_pattern" name="sleep_pattern" value={formData.sleep_pattern} onChange={handleChange} rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exercise_routine">Exercise Routine</Label>
                <Input id="exercise_routine" name="exercise_routine" value={formData.exercise_routine} onChange={handleChange} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="substance_use">Substance Use</Label>
                <Textarea id="substance_use" name="substance_use" value={formData.substance_use} onChange={handleChange} rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="support_system">Support System</Label>
                <Textarea id="support_system" name="support_system" value={formData.support_system} onChange={handleChange} rows={2} />
              </div>
            </CardContent>
          </Card>

          {/* Safety */}
          <Card className="border-red-200 bg-red-50 print-keep-color">
            <CardHeader>
              <CardTitle className="text-red-800">Safety Assessment</CardTitle>
              <CardDescription className="text-red-700">Important questions about your safety</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="suicidal_thoughts">Thoughts of Suicide or Self-Harm</Label>
                <Textarea id="suicidal_thoughts" name="suicidal_thoughts" value={formData.suicidal_thoughts} onChange={handleChange} rows={3} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="self_harm">History of Self-Harm</Label>
                <Textarea id="self_harm" name="self_harm" value={formData.self_harm} onChange={handleChange} rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_plan">Emergency Plan</Label>
                <Textarea id="emergency_plan" name="emergency_plan" value={formData.emergency_plan} onChange={handleChange} rows={3} />
              </div>
              <div className="bg-white p-4 rounded-lg border border-red-300">
                <p className="text-sm text-red-800 font-semibold mb-2">If you're in crisis:</p>
                <p className="text-sm text-red-700">
                  Please call emergency services (112) or a mental health helpline immediately.
                  Therapy is not a substitute for emergency care.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons (no-print) */}
          <div className="no-print flex flex-col sm:flex-row gap-4 sticky bottom-4 bg-white/95 backdrop-blur p-4 rounded-xl shadow-lg border border-slate-200">
            <Button
              type="button"
              onClick={handleSaveAsPDF}
              data-testid="save-as-pdf-btn"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-base py-6 gap-2"
            >
              <Printer className="w-5 h-5" />
              Save as PDF
            </Button>
            <Button
              type="button"
              onClick={handleSendWhatsApp}
              data-testid="send-whatsapp-btn"
              className="flex-1 bg-green-600 hover:bg-green-700 text-base py-6 gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Send via WhatsApp
            </Button>
          </div>

          <p className="no-print text-center text-sm text-slate-500 mt-2">
            After saving the PDF, email it to <strong>hello@mfcounsellingservices.com</strong> or send it on WhatsApp to <strong>+91 99119 22763</strong>.
          </p>
        </form>
      </div>
    </div>
  );
};

export default PublicIntakeForm;
